// /reports/account-summary — replicates the legacy "Account Summary" tab.
// Pivot: rows = FRT Customer (sectioned by category), columns = account_type
// codes + tiller_status codes (two side-by-side panels).

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { reports } from '@/lib/api';

function currentMonth(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function AccountSummaryPage() {
  const [mode, setMode] = useState<'current' | 'period'>('current');
  const [period, setPeriod] = useState<string>(currentMonth());
  const asOf = mode === 'current' ? 'current' : period;

  const q = useQuery({
    queryKey: ['reports', 'account-summary', asOf],
    queryFn: () => reports.accountSummary(asOf),
  });

  const data = q.data;

  const onExport = useMemo(
    () => async (format: 'xlsx' | 'csv') => {
      try {
        await reports.accountSummaryExport(asOf, format);
      } catch (err) {
        console.error(err);
      }
    },
    [asOf],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Account Summary</h1>
        <div className="flex items-center gap-2 text-sm">
          <label>
            <input
              type="radio"
              checked={mode === 'current'}
              onChange={() => setMode('current')}
              className="mr-1"
            />
            Live
          </label>
          <label>
            <input
              type="radio"
              checked={mode === 'period'}
              onChange={() => setMode('period')}
              className="mr-1"
            />
            As of period
          </label>
          <input
            type="month"
            value={period}
            disabled={mode !== 'period'}
            onChange={(e) => setPeriod(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 disabled:bg-slate-100"
          />
          <Button size="sm" variant="secondary" onClick={() => onExport('xlsx')}>
            Export XLSX
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onExport('csv')}>
            Export CSV
          </Button>
        </div>
      </div>

      {q.isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : q.error ? (
        <div className="text-sm text-red-600">{(q.error as Error).message}</div>
      ) : data ? (
        <div className="grid grid-cols-1 gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Total Accounts in Network</CardTitle>
            </CardHeader>
            <CardBody className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-1 pl-1 text-left">FRT Customer</th>
                    {data.account_type_codes.map((c) => (
                      <th key={c} className="py-1 px-2 text-right">
                        {c}
                      </th>
                    ))}
                    <th className="py-1 pr-1 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sections.map((section) => (
                    <SectionRows key={section.frt_category_name} section={section} typeCodes={data.account_type_codes} mode="account_type" />
                  ))}
                  <tr className="border-t-2 border-slate-300 font-semibold">
                    <td className="py-1 pl-1">Grand Total</td>
                    {data.account_type_codes.map((c) => (
                      <td key={c} className="py-1 px-2 text-right tabular-nums">
                        {data.grand_totals.by_account_type[c] ?? 0}
                      </td>
                    ))}
                    <td className="py-1 pr-1 text-right tabular-nums">
                      {data.grand_totals.total_accounts}
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardBody>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Account Status</CardTitle>
            </CardHeader>
            <CardBody className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-1 pl-1 text-left">FRT Customer</th>
                    {data.tiller_status_codes.map((c) => (
                      <th key={c} className="py-1 px-2 text-right">
                        {c}
                      </th>
                    ))}
                    <th className="py-1 pr-1 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sections.map((section) => (
                    <SectionRows key={section.frt_category_name} section={section} typeCodes={data.tiller_status_codes} mode="tiller_status" />
                  ))}
                  <tr className="border-t-2 border-slate-300 font-semibold">
                    <td className="py-1 pl-1">Grand Total</td>
                    {data.tiller_status_codes.map((c) => (
                      <td key={c} className="py-1 px-2 text-right tabular-nums">
                        {data.grand_totals.by_tiller_status[c] ?? 0}
                      </td>
                    ))}
                    <td className="py-1 pr-1 text-right tabular-nums">
                      {data.grand_totals.total_accounts}
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardBody>
          </Card>
        </div>
      ) : null}
    </div>
  );
}

interface Section {
  frt_category_name: string;
  customers: Array<{
    frt_customer_id: number;
    frt_customer_code: string;
    frt_customer_name: string;
    by_account_type: Record<string, number>;
    by_tiller_status: Record<string, number>;
    total_accounts: number;
  }>;
  section_totals: {
    by_account_type: Record<string, number>;
    by_tiller_status: Record<string, number>;
    total_accounts: number;
  };
}

function SectionRows({
  section,
  typeCodes,
  mode,
}: {
  section: Section;
  typeCodes: readonly string[];
  mode: 'account_type' | 'tiller_status';
}) {
  return (
    <>
      <tr>
        <td colSpan={typeCodes.length + 2} className="bg-slate-50 px-1 py-1 text-xs uppercase tracking-wide text-slate-500">
          {section.frt_category_name}
        </td>
      </tr>
      {section.customers.map((cust) => (
        <tr key={cust.frt_customer_id} className="border-t border-slate-100">
          <td className="py-1 pl-1">
            <span className="font-medium">{cust.frt_customer_code}</span>
            <span className="ml-2 text-slate-500">{cust.frt_customer_name}</span>
          </td>
          {typeCodes.map((c) => (
            <td key={c} className="py-1 px-2 text-right tabular-nums">
              {(mode === 'account_type' ? cust.by_account_type : cust.by_tiller_status)[c] ?? 0}
            </td>
          ))}
          <td className="py-1 pr-1 text-right tabular-nums">{cust.total_accounts}</td>
        </tr>
      ))}
      <tr className="border-t border-slate-200 bg-slate-50 text-slate-700">
        <td className="py-1 pl-1 italic">Section total</td>
        {typeCodes.map((c) => (
          <td key={c} className="py-1 px-2 text-right tabular-nums">
            {(mode === 'account_type'
              ? section.section_totals.by_account_type
              : section.section_totals.by_tiller_status)[c] ?? 0}
          </td>
        ))}
        <td className="py-1 pr-1 text-right tabular-nums">
          {section.section_totals.total_accounts}
        </td>
      </tr>
    </>
  );
}
